import React from "react";
import { List, Card, Button, Spin, Switch, Avatar } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";

import Base from "../../Base";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import EditNavForm from "./EditNavForm";

const { Meta } = Card;

class NavigatorManagement extends Base {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      data: null,
      navData: null,
      loading: false,
      currentPage: this.getRouterQueryPage(),
      currentPageSize: this.getRouterQueryPageSize(12),
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.onGetNavsList();
    this.loadCategories();
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);
    const { currentPage, currentPageSize } = this.state;

    if (
      prevState.currentPage !== currentPage ||
      prevState.currentPageSize !== currentPageSize
    ) {
      this.showMessage(this.i18n.t("proxy.updatingData"), Base.Message.loading);
      this.setRouterQuery({
        page: currentPage,
        pageSize: currentPageSize,
      });
      this.onGetNavsList();
    }
  }

  async loadCategories() {
    const token = this.getUserToken();
    if (!token) return;
    const api = this.api.product.merchantCategories;
    const apiConfig = {
      token,
    };

    try {
      const res = await api.run(apiConfig);

      if (!this.mounted) return;

      if (res.result && res.data && res.data.length > 0) {
        this.shopCats = res.data.map(({ id, ug_name, zh_name }) => ({
          id,
          ug_name,
          zh_name,
        }));
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
    }
  }

  async onGetNavsList() {
    const token = this.getUserToken();
    if (!token) return;

    const { currentPageSize, currentPage } = this.state;
    const api = this.api.navs.list;
    const apiConfig = {
      token,
      query: {
        page: currentPage,
        count: currentPageSize,
      },
    };

    try {
      this.setState({ loading: true });
      const res = await api.run(apiConfig);
      if (!this.mounted) return;

      if (res.result) {
        this.setState({ data: res.data });
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ loading: false, navData: null });
    }
  }

  onHandleCancel = () => {
    this.setState({ navData: null });
  };

  onAddNewAd = () => {
    this.setState({ navData: {} });
  };

  onPaginationPageChange = (page, pageSize) => {
    this.setState({ currentPage: page });
  };

  onPaginationPageSizeChange = (page, pageSize) => {
    this.setState({ currentPageSize: pageSize, currentPage: 1 });
  };

  getAdStatus(status) {
    if (typeof status === "number") return status;
    return status ? 1 : 0;
  }

  onFinishForm = async (values) => {
    const token = this.getUserToken();
    if (!token) return;

    const { zh_name, ug_name, order, status, show_text, icon_height, target, tab_id, app_id, room_id } =
      values;
    let targetUrl = `${target}?tabId=${tab_id}`;
    if (room_id) {
      targetUrl = `${target}?room_id=${room_id}`;
    }
    if (app_id) {
      targetUrl = `${target}?app_id=${app_id}`;
    }
    let link_data;
    if (values?.data && this.state.navData?.data != values?.data) {
      link_data = values.data;
      targetUrl = values.data.url;
    }
    let api = this.api.navs.add;
    let apiConfig = {
      token,
      data: {
        zh_name,
        ug_name,
        order,
        target: targetUrl,
        status: this.getAdStatus(status),
        show_text: this.getAdStatus(show_text),
        icon_height,
        link_data,
      },
    };

    if (values.image && values.image.length > 0) {
      apiConfig.data.image = values["image"][0]["file_path"];
    }

    const { navData } = this.state;
    if (navData && navData.id) {
      api = this.api.navs.update;
      apiConfig.path = { id: navData.id };
    }

    this.setState({ loading: true });
    try {
      const res = await api.run(apiConfig);
      if (!this.mounted) return;
      if (res.result) {
        this.onGetNavsList();
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ loading: false });
    }
  };

  onDeleteNav(id) {
    this.showModal(
      this.i18n.t("sales.deleteWarning"),
      Base.Modal.confirm,
      async () => {
        const token = this.getUserToken();
        if (!token) return;

        const api = this.api.navs.delete;
        const apiConfig = {
          token,
          path: { id },
        };
        try {
          const res = await api.run(apiConfig);
          if (res.result) {
            this.onGetNavsList();
          } else {
            this.showMessage(res.message);
          }
        } catch (error) {
          if (!this.mounted) return;
          this.handleApiError(api, error);
        }
      }
    );
  }

  onEditNav = (navData) => {
    this.setState({ navData });
  };

  async onChangeNavStatus(item) {
    const token = this.getUserToken();
    if (!token) return;

    let api = this.api.navs.update;
    delete item["image"];
    let apiConfig = {
      token,
      path: { id: item.id },
      data: {
        ...item,
        status: item.status === 0 ? 1 : 0,
      },
    };

    this.setState({ loading: true });
    try {
      const res = await api.run(apiConfig);
      if (!this.mounted) return;
      if (res.result) {
        this.onGetNavsList();
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ loading: false });
    }
  }

  render() {
    const { currentPage, currentPageSize, data, navData, loading } = this.state;

    return (
      <Spin spinning={loading}>
        <List
          pagination={{
            current: currentPage,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSize: currentPageSize,
            pageSizeOptions: ["12", "24", "36"],
            onChange: this.onPaginationPageChange,
            onShowSizeChange: this.onPaginationPageSizeChange,
            total: data ? data.total : 0,
            style: {
              paddingBottom: 20,
            },
          }}
          header={
            <Button size="large" type="primary" onClick={this.onAddNewAd}>
              {this.i18n.t("sales.nav.add")}
            </Button>
          }
          grid={{
            gutter: 12,
            xs: 1,
            sm: 1,
            md: 1,
            lg: 2,
            xl: 3,
            xxl: 4,
          }}
          dataSource={data ? data.data : []}
          renderItem={(item) => (
            <List.Item>
              <Card
                hoverable
                style={{ flexDirection: "column" }}
                actions={[
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                    checked={item.status !== 0}
                    onClick={() => this.onChangeNavStatus(item)}
                  />,
                  <EditOutlined
                    key="edit"
                    onClick={() => this.onEditNav(item)}
                  />,
                  <DeleteOutlined
                    key="delete"
                    onClick={() => this.onDeleteNav(item.id)}
                  />,
                ]}
              >
                <Meta
                  avatar={<Avatar src={item.image} size="large"></Avatar>}
                  title={`${item.zh_name}/${item.ug_name}`}
                  description={item.target}
                />
              </Card>
            </List.Item>
          )}
        />
        <EditNavForm
          navData={navData}
          onCancel={this.onHandleCancel}
          onFinish={this.onFinishForm}
          loading={loading}
          shopCats={this.shopCats}
          showExtraOptions={
            this.isBBShopMerchant() || this.isThirdPartyShopHasWeapp()
          }
          showAgent={this.shopHasAgents()}
        />
      </Spin>
    );
  }
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(NavigatorManagement);
